import type { ReactNode } from 'react'
import { cx } from './controls/cx'
import s from './DataTable.module.css'

/**
 * A SEMANTIC DATA TABLE — the accessible twin of a chart, and nothing else.
 *
 * ── what this is, and what it is NOT ───────────────────────────────────────
 * `DataGrid` is the interactive collection grammar: selection, sort, a roving
 * tabindex, editable cells, a column menu. This is the other thing a table can
 * be — a static, readable rendering of rows that a customer or an assistive
 * technology reads rather than operates. A chart's "Data table" view is exactly
 * that, and expressing it as a `DataGrid` would promise interaction the view
 * does not have.
 *
 * It exists because that markup was being retyped. `DashboardWidget` carried a
 * private `RowTable`; `EstatePage` hand-wrote a second one with its own
 * `.insightTable` rules in a feature stylesheet, no `<caption>`, and figures
 * right-aligned by position. Both now pass data. The console gallery keeps a
 * third `<table>` deliberately: it is one measure under a `<th scope="row">`,
 * a row-header shape this component does not render, on a page whose rasters
 * are baselined — converting it would change reviewed pixels and answer no
 * question.
 *
 * ── the alignment rule, and why it is a column and not a selector ──────────
 * The estate stylesheet right-aligned figures with
 * `td:nth-last-child(-n + 2)` — "the last two columns are numbers". That is
 * true of the table it was written against and false the moment a column is
 * added or reordered, silently, in the direction of a misread figure. A column
 * says whether IT is a number, so the answer travels with the declaration.
 */
export interface DataTableColumn<R = Record<string, unknown>> {
  key: string
  label: string
  /** Right-aligned and tabular-figured: a quantity, not a word. */
  numeric?: boolean
  /** Renders the cell. Defaults to the row's value under `key`. */
  cell?: (row: R) => ReactNode
}

export interface DataTableProps<R = Record<string, unknown>> {
  /** Required, and visible. A table with no caption is a table whose subject
   *  only the surrounding page knows — which a screen reader is not reading. */
  caption: ReactNode
  columns: readonly DataTableColumn<R>[]
  rows: readonly R[]
  rowKey?: (row: R, index: number) => string
  /** Opens the row from its FIRST cell, where one is offered. */
  onOpen?: (row: R) => void
  canOpen?: (row: R) => boolean
  className?: string
  testId?: string
}

/**
 * An absent value reads as a dash, never as an empty cell or a bare `null`.
 *
 * Exported because the same rule has to hold in a list that shows the same rows
 * — `DashboardWidget` renders its rows twice, as a list and as this table, and
 * two definitions of "nothing to show" is how the two views come to disagree.
 */
export const cellText = (value: unknown): ReactNode =>
  value == null || value === '' ? '—' : String(value)

export function DataTable<R extends Record<string, unknown>>({
  caption, columns, rows, rowKey, onOpen, canOpen, className, testId,
}: DataTableProps<R>) {
  /* The table sits in its OWN scroll box (#2512). A card clips what escapes
     it (`Card` is `overflow: hidden`), so a table one column too wide for a
     phone lost its last column — the Connected apps "Disconnect" drew as
     "Disco". Where the table fits, the box changes nothing; where it does not,
     the table scrolls sideways inside it and the page never does. */
  return (
    <div className={s.scroll} data-table-scroll="">
      <table className={cx(s.table, className)} data-testid={testId}>
        <caption className={s.caption}>{caption}</caption>
        <thead>
          <tr>
            {columns.map((column) => (
              <th key={column.key} scope="col" data-numeric={column.numeric ? '' : undefined}>
                {column.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, index) => (
            <tr key={rowKey?.(row, index) ?? String(index)}>
              {columns.map((column, columnIndex) => {
                const content = column.cell ? column.cell(row) : cellText(row[column.key])
                return (
                  <td key={column.key} data-numeric={column.numeric ? '' : undefined}>
                    {columnIndex === 0 && onOpen && (canOpen?.(row) ?? true)
                      ? <button type="button" onClick={() => onOpen(row)}>{content}</button>
                      : content}
                  </td>
                )
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}
